import { TaskStatus } from "@/enums/TaskStatus";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { useSessionStorage } from "@/hooks/useSessionStorage";
import { SessionStorageConstants } from "@/types/SessionStorageConstants";
import { TaskFiltersDTO } from "@/types/TaskFiltersDTO";
import { TaskRowInterface } from "@/types/TaskRowInterface";
import { Dispatch, PropsWithChildren, SetStateAction, createContext, useContext, useEffect, useState } from "react";
import { LazoView, useLazoView } from "./CurrentLazoViewContext";

export type TaskFilterMap = { [k: string]: TaskFilterOptions };
export type TaskFilterOptions = { [ko in TaskFilterId]: TaskFilterOption };
export type TaskFilterOption = { render: string; value: any; valueRenders?: any[] };

export type UpdateFilterFn = (tab: TaskTabs, id: TaskFilterId, value: any) => void;
export type UpdateAllFiltersFn = (tab: TaskTabs, filters: TaskFilterOptions) => void;
export type UpdateAllFiltersWithDTOFn = (tab: TaskTabs, filters: TaskFiltersDTO) => void;

export type TaskSort = { orderBy: keyof TaskRowInterface; order: "asc" | "desc" };

type TaskFilterContextType = {
  selectedFilters: TaskFilterMap;
  updateFilter: UpdateFilterFn;
  updateAllFilters: UpdateAllFiltersFn;
  updateAllFiltersWithDTO: UpdateAllFiltersWithDTOFn;
  sortData: TaskSort;
  updateSortData: (id: keyof TaskRowInterface) => void;
  savedData: TaskFilterSaveState;
  setSavedData: Dispatch<SetStateAction<TaskFilterSaveState>>;
  selectedTaskTab: TaskTabs;
  setSelectedTaskTab: Dispatch<SetStateAction<TaskTabs>>;
};

const TaskFiltersContext = createContext<TaskFilterContextType>(undefined);

export enum TaskTabs {
  // Admin tabs
  ALL_TASKS = "All Tasks",
  MY_WAITING_FOR_REVIEW = "My Waiting for Review",
  MY_IN_PROGRESS = "My In Progress",
  MY_OVERDUE = "My Overdue",
  MY_DONE = "My Done",
  TASKS_WITH_OPEN_COMMENTS = "My Tasks with Open Comments",
  CLIENTS_OVERDUE = "Overdue Client Tasks",
  ASSIGNED_TO_ME = "Assigned to me",
  IN_PROCESS = "In Process",

  // One tabs
  ONGOING_TASKS = "Ongoing Tasks",
  DONE_TASKS = "Done Tasks",
}

export const ADMIN_TASK_TABS = [
  "ALL_TASKS",
  "MY_WAITING_FOR_REVIEW",
  "MY_IN_PROGRESS",
  "MY_OVERDUE",
  "MY_DONE",
  "TASKS_WITH_OPEN_COMMENTS",
  "CLIENTS_OVERDUE",
  "ASSIGNED_TO_ME",
  "IN_PROCESS",
];

export const ONE_TASK_TABS = ["ONGOING_TASKS", "DONE_TASKS"];

const ONE_ONGOING_DEFAULT_STATUSES: TaskStatus[] = [
  TaskStatus.IN_PROGRESS,
  TaskStatus.OVERDUE,
  TaskStatus.PENDING,
  TaskStatus.WAITING_FOR_REVIEW,
];

export type TaskFilterId =
  | "service"
  | "people"
  | "taskType"
  | "status"
  | "tenant"
  | "from"
  | "to"
  | "search"
  | "withUnansweredComments"
  | "lists"
  | "module";

export const PORTAL_TASK_DEFAULT_FILTERS: TaskFilterOptions = {
  service: {
    render: "Service",
    value: [],
  },
  people: {
    render: "People",
    value: [],
  },
  taskType: {
    render: "Task Type",
    value: [],
  },
  status: {
    render: "Task Status",
    value: [TaskStatus.IN_PROGRESS, TaskStatus.PENDING, TaskStatus.OVERDUE, TaskStatus.WAITING_FOR_REVIEW],
  },
  tenant: {
    render: "Company",
    value: [],
  },
  from: {
    render: "-",
    value: [],
  },
  to: {
    render: "-",
    value: [],
  },
  search: {
    render: "Search",
    value: "",
  },
  withUnansweredComments: {
    render: "-",
    value: false,
  },
  lists: {
    render: "Lists",
    value: [],
  },
  module: {
    render: "Module",
    value: [],
  },
};

export const ONE_TASK_DEFAULT_FILTERS: TaskFilterOptions = {
  service: {
    render: "Service",
    value: [],
  },
  people: {
    render: "People",
    value: [],
  },
  taskType: {
    render: "Task Type",
    value: [],
  },
  status: {
    render: "Task Status",
    value: [...ONE_ONGOING_DEFAULT_STATUSES],
  },
  tenant: {
    render: "Company",
    value: [],
  },
  from: {
    render: "-",
    value: [],
  },
  to: {
    render: "-",
    value: [],
  },
  search: {
    render: "Search",
    value: "",
  },
  withUnansweredComments: {
    render: "-",
    value: false,
  },
  lists: {
    render: "Lists",
    value: [],
  },
  module: {
    render: "Module",
    value: [],
  },
};

export function getDefaultFiltersForTab(isOne: boolean, tab: TaskTabs): TaskFilterOptions {
  const base = isOne ? ONE_TASK_DEFAULT_FILTERS : PORTAL_TASK_DEFAULT_FILTERS;
  const clone = structuredClone(base);

  if (isOne && tab === TaskTabs.ONGOING_TASKS) {
    clone.status.value = [...ONE_ONGOING_DEFAULT_STATUSES];
  }

  if (tab === TaskTabs.TASKS_WITH_OPEN_COMMENTS) {
    clone.status.value = Object.values(TaskStatus);
  }

  return clone;
}

const DEFAULT_SORT_DATA = { orderBy: "dueDate", order: "desc" } as const;
const DEFAULT_SELECTED_TAB = TaskTabs.ALL_TASKS;

type TaskFilterSaveState = {
  sortData: TaskSort;
  filterData: TaskFiltersDTO;
};

export function TaskFiltersProvider({ children }: PropsWithChildren) {
  const isOne = useLazoView().lazoView === LazoView.LAZO_ONE;
  const DEFAULT_FILTERS_MAP = Object.fromEntries(Object.values(TaskTabs).map((k) => [k, getDefaultFiltersForTab(isOne, k as TaskTabs)]));
  const userExternalId = AuthService.getUser()?.externalId || "anonymous";
  const filtersStorageKey = `${SessionStorageConstants.ADMIN_TASKS_FILTERS}.${userExternalId}`;
  const selectedTabStorageKey = `${SessionStorageConstants.ADMIN_TASKS_SELECTED_TAB}.${userExternalId}`;

  const [selectedFilters, setSelectedFilters] = useSessionStorage<TaskFilterMap>(filtersStorageKey, DEFAULT_FILTERS_MAP);
  const [sortData, setSortData] = useState<TaskSort>(DEFAULT_SORT_DATA);
  const [selectedTaskTab, setSelectedTaskTab] = useSessionStorage<TaskTabs>(selectedTabStorageKey, DEFAULT_SELECTED_TAB);

  const [savedData, setSavedData] = useState<TaskFilterSaveState>({ sortData: null, filterData: null });

  useEffect(() => {
    if (!isOne) return;

    setSelectedFilters((prev) => {
      const clone = structuredClone(prev);
      const ongoingFilters = clone[TaskTabs.ONGOING_TASKS];

      if (!ongoingFilters) {
        clone[TaskTabs.ONGOING_TASKS] = getDefaultFiltersForTab(true, TaskTabs.ONGOING_TASKS);
        return clone;
      }

      const currentStatuses = (ongoingFilters.status?.value || []) as TaskStatus[];
      const hasLegacySelectionWithoutPending =
        !currentStatuses.includes(TaskStatus.PENDING) &&
        currentStatuses.includes(TaskStatus.IN_PROGRESS) &&
        currentStatuses.includes(TaskStatus.OVERDUE) &&
        currentStatuses.includes(TaskStatus.WAITING_FOR_REVIEW) &&
        !currentStatuses.includes(TaskStatus.DONE);

      const shouldApplyDefaultSelection = currentStatuses.length === 0 || hasLegacySelectionWithoutPending;

      if (!shouldApplyDefaultSelection) {
        return prev;
      }

      ongoingFilters.status.value = [...ONE_ONGOING_DEFAULT_STATUSES];
      return clone;
    });
  }, [isOne, setSelectedFilters]);

  function updateFilter(tab: TaskTabs, id: TaskFilterId, value: any) {
    setSelectedFilters((prev) => {
      let clone = structuredClone(prev);
      clone[tab][id].value = value;
      return clone;
    });
  }

  function updateAllFilters(tab: TaskTabs, filters: TaskFilterOptions) {
    setSelectedFilters((prev) => {
      let clone = structuredClone(prev);
      clone[tab] = filters;
      return clone;
    });
  }

  function updateAllFiltersWithDTO(tab: TaskTabs, filters: TaskFiltersDTO) {
    setSelectedFilters((prev) => {
      let clone = structuredClone(prev);
      clone[tab].to.value = filters.to || [];
      clone[tab].from.value = filters.from || [];
      clone[tab].people.value = filters.assignedExternalIds || [];
      clone[tab].search.value = filters.search || "";
      clone[tab].status.value = filters.statuses || [];
      clone[tab].taskType.value = filters.types || [];
      clone[tab].withUnansweredComments.value = filters.withUnansweredComments || false;
      clone[tab].service.value = filters.services || [];
      clone[tab].tenant.value = filters.tenantsNames || [];
      clone[tab].lists.value = filters.lists || [];
      return clone;
    });
  }

  function updateSortData(orderBy: keyof TaskRowInterface) {
    let newOrder = sortData.order;
    if (orderBy === sortData.orderBy) {
      newOrder = sortData.order === "asc" ? "desc" : "asc";
      setSortData((prev) => ({ ...prev, order: newOrder }));
    } else {
      setSortData((prev) => ({ ...prev, orderBy: orderBy }));
    }
  }

  return (
    <TaskFiltersContext.Provider
      value={{
        selectedFilters,
        updateFilter,
        updateAllFilters,
        updateAllFiltersWithDTO,
        sortData,
        updateSortData,
        savedData,
        setSavedData,
        selectedTaskTab,
        setSelectedTaskTab,
      }}
    >
      {children}
    </TaskFiltersContext.Provider>
  );
}

export function useTaskFilters() {
  const context = useContext(TaskFiltersContext);
  if (!context) {
    throw new Error("useTaskFilters must be used within a FiltersProvider");
  }
  return context;
}
